import "./BulkUploadModal.css";

import { FormEvent } from "react";

import { ErrorBox, InformationBox } from "../../cards";
import { ActionButtons, FormLabel, KanaTooltip, MultipleFileUpload } from "../../forms";
import { Column, Row } from "../../layout";
import { TextLink } from "../../navigation";
import { BulkUploadFieldsProps } from "./models";

// The body of the bulk upload form, shared by the modal and the inline card on the project builder
// so the two cannot drift apart.
export const BulkUploadFields = ({
  onDownloadTemplate,
  onFileSelected,
  onUpload,
  onCancel,
  isParsing = false,
  parseError,
  showOutOfDateTemplateHint = false,
}: BulkUploadFieldsProps): JSX.Element => {
  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onUpload();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="BulkUploadModal_content">
        <p className="body2">
          Upload an XLSX of your projects. We&apos;ll map the columns to Kana&apos;s fields and flag anything that needs
          fixing before anything is created.
        </p>

        {parseError && <ErrorBox title="We could not read that file" text={parseError} />}

        {showOutOfDateTemplateHint && (
          <InformationBox variant="warning">
            <p className="body2">
              This template looks out of date. You can still upload it, but downloading a fresh template avoids missing
              columns.
            </p>
          </InformationBox>
        )}

        <div className="BulkUploadModal_upload">
          <FormLabel
            label="Single file upload"
            tooltip={
              <KanaTooltip
                placement="bottomLeft"
                tooltipHeader="Single file upload"
                tooltipText="Upload one .xlsx at a time. Every project in it is read together, so keep them all on the same sheet."
              />
            }
          >
            <MultipleFileUpload
              title=""
              accept=".xlsx"
              maxFileCount={1}
              onChange={(previewFiles) => {
                const [previewFile] = previewFiles;
                onFileSelected(previewFile?.file ?? null);
              }}
            />
          </FormLabel>
          <p className="body3 BulkUploadModal_template">
            <TextLink onAction={onDownloadTemplate}>Download template</TextLink>
          </p>
        </div>

        <Row justify="center">
          <Column span={6} hasNoPadding>
            <ActionButtons
              fullWidth
              submitButtonLabel="Upload file"
              onCancel={onCancel}
              isLoading={isParsing}
              loadingText="Reading file"
              disabled={isParsing}
            />
          </Column>
        </Row>
      </div>
    </form>
  );
};
